import { loginError, type SessionReplacement } from './sessionLimit';
/**
 * JWT Authentication Service
 * Handles token storage, retrieval, and refresh logic
 *
 * SECURITY: Cookie-Based Refresh Token Storage
 * =============================================
 * This implementation uses httpOnly cookies for refresh token storage,
 * which protects against XSS (Cross-Site Scripting) attacks.
 *
 * Security features:
 * 1. Refresh tokens stored in httpOnly cookies (not accessible via JavaScript)
 * 2. Access tokens stored in-memory only (not persisted to localStorage)
 * 3. SameSite=Strict cookies prevent CSRF attacks
 * 4. Short access token lifetime (15 minutes)
 * 5. Refresh token rotation with blacklisting
 *
 * The cookie-based endpoints (/api/auth/*) are used by default for web clients.
 * The legacy endpoints (/api/login/, /api/logout/) remain available for the
 * mobile app which requires tokens in the response body.
 */

interface AuthTokens {
  access: string;
  refresh?: string; // Optional - only present in legacy mode
}

interface LoginResponse {
  detail: string;
  access: string;
  refresh?: string; // Optional - only present in legacy mode
  user: {
    id: number;
    username: string;
    email: string;
    organization: string | null;
    is_affiliate?: boolean;
    is_superuser?: boolean;
  };
}

interface AuthStatusResponse {
  has_refresh_token: boolean;
  is_authenticated: boolean;
}

// Legacy localStorage key - only used for migration cleanup
const LEGACY_TOKEN_STORAGE_KEY = '@apexos:tokens';

/** Web Lock name — shared across tabs in the same origin. */
const REFRESH_LOCK_NAME = 'apexos-auth-refresh';

/** BroadcastChannel name for sharing a freshly rotated access token. */
const AUTH_CHANNEL_NAME = 'apexos-auth';

/** How many times to POST /api/auth/refresh/ (initial attempt + retries). */
const REFRESH_ATTEMPTS = 3;

/** Backoff before retry attempts 2 and 3 (ms). */
const REFRESH_RETRY_DELAYS_MS = [300, 700] as const;

/** Refresh this long before the access token expires (capped at half its lifetime). */
const PROACTIVE_REFRESH_LEAD_MS = 60_000;

/** Never schedule a proactive refresh sooner than this, so a bad token can't cause a refresh loop. */
const MIN_PROACTIVE_REFRESH_DELAY_MS = 10_000;

/** A token with less validity than this left is treated as expiring. */
const EXPIRING_THRESHOLD_MS = 30_000;

interface JwtTimes {
  /** Issued-at, ms since epoch */
  iat?: number;
  /** Expiry, ms since epoch */
  exp: number;
}

/** Read `iat`/`exp` from a JWT without verifying it (the server does that). */
function readJwtTimes(token: string): JwtTimes | null {
  try {
    const payload = token.split('.')[1];
    if (!payload) {
      return null;
    }
    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    const claims = JSON.parse(json) as { iat?: unknown; exp?: unknown };
    if (typeof claims.exp !== 'number') {
      return null;
    }
    return {
      exp: claims.exp * 1000,
      iat: typeof claims.iat === 'number' ? claims.iat * 1000 : undefined,
    };
  } catch {
    return null;
  }
}

type AuthBroadcastMessage =
  | { type: 'refresh-done'; access: string; at: number }
  | { type: 'refresh-failed'; at: number };

export class AuthService {
  private static instance: AuthService;

  // In-memory storage for access token (XSS-safe)
  private accessToken: string | null = null;

  // Track if we have a valid session (refresh token cookie exists)
  private hasSession: boolean = false;

  // Single-flight lock: when a refresh is already in progress, concurrent
  // callers await the same promise instead of firing parallel POSTs.
  // Parallel refreshes race server-side token rotation (the loser's token is
  // already blacklisted), which used to destroy valid sessions and burn
  // through the per-IP rate limit.
  private refreshPromise: Promise<string> | null = null;

  // Cross-tab coordination: another tab may finish a refresh while we wait
  // for the Web Lock / retry window. Keep the newest broadcast token briefly.
  private peerAccessToken: string | null = null;
  private peerAccessTokenAt = 0;
  private authChannel: BroadcastChannel | null = null;

  // Refreshes the access token shortly before it expires, so the many callers
  // that read getAccessToken() directly (WebSockets, raw fetch helpers) never
  // hold an expired token while the page is open.
  private proactiveRefreshTimer: ReturnType<typeof setTimeout> | null = null;

  private constructor() {
    // On initialization, clean up any legacy localStorage tokens
    this.migrateLegacyTokens();
    this.setupAuthChannel();
    this.setupWakeRefresh();
  }

  /**
   * Timers are paused while a laptop sleeps and throttled in background tabs,
   * so the proactive refresh can be late. Catch up when the tab is shown again.
   */
  private setupWakeRefresh(): void {
    if (typeof document === 'undefined' || typeof document.addEventListener !== 'function') {
      return;
    }
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        void this.ensureFreshAccessToken();
      }
    });
  }

  private scheduleProactiveRefresh(token: string): void {
    this.cancelProactiveRefresh();
    const times = readJwtTimes(token);
    if (!times) {
      return;
    }
    // Measure the lifetime from the token's own iat/exp so client clock skew
    // can't make a fresh token look expired. Fall back to the local clock.
    const lifetime = times.iat !== undefined ? times.exp - times.iat : times.exp - Date.now();
    const lead = Math.min(PROACTIVE_REFRESH_LEAD_MS, lifetime / 2);
    const delay = Math.max(lifetime - lead, MIN_PROACTIVE_REFRESH_DELAY_MS);
    this.proactiveRefreshTimer = setTimeout(() => {
      this.proactiveRefreshTimer = null;
      // A failure here is handled by the 401 path on the next request.
      this.refreshAccessToken().catch(() => {});
    }, delay);
  }

  private cancelProactiveRefresh(): void {
    if (this.proactiveRefreshTimer !== null) {
      clearTimeout(this.proactiveRefreshTimer);
      this.proactiveRefreshTimer = null;
    }
  }

  private setupAuthChannel(): void {
    if (typeof BroadcastChannel === 'undefined') {
      return;
    }
    try {
      this.authChannel = new BroadcastChannel(AUTH_CHANNEL_NAME);
      this.authChannel.onmessage = (event: MessageEvent<AuthBroadcastMessage>) => {
        const data = event.data;
        if (!data || typeof data !== 'object') {
          return;
        }
        if (data.type === 'refresh-done' && typeof data.access === 'string') {
          this.peerAccessToken = data.access;
          this.peerAccessTokenAt = data.at || Date.now();
          this.setAccessToken(data.access);
        }
      };
    } catch {
      this.authChannel = null;
    }
  }

  private broadcastRefreshResult(access: string | null): void {
    if (!this.authChannel) {
      return;
    }
    const message: AuthBroadcastMessage = access
      ? { type: 'refresh-done', access, at: Date.now() }
      : { type: 'refresh-failed', at: Date.now() };
    try {
      this.authChannel.postMessage(message);
    } catch {
      // Channel may be closed in tests / teardown
    }
  }

  /** Prefer a peer tab's token if it was broadcast within the last few seconds. */
  private takeRecentPeerToken(): string | null {
    if (!this.peerAccessToken) {
      return null;
    }
    if (Date.now() - this.peerAccessTokenAt > 5000) {
      this.peerAccessToken = null;
      return null;
    }
    const token = this.peerAccessToken;
    this.peerAccessToken = null;
    return token;
  }

  static getInstance(): AuthService {
    if (!AuthService.instance) {
      AuthService.instance = new AuthService();
    }
    return AuthService.instance;
  }

  /**
   * Migrate from localStorage to cookie-based auth
   * Cleans up old tokens from localStorage
   */
  private migrateLegacyTokens(): void {
    const legacyTokens = localStorage.getItem(LEGACY_TOKEN_STORAGE_KEY);
    if (legacyTokens) {
      // Clear legacy tokens - user will need to re-login with cookie-based auth
      localStorage.removeItem(LEGACY_TOKEN_STORAGE_KEY);
      console.info('[Auth] Migrated from localStorage to cookie-based auth');
    }
  }

  /**
   * Store access token in memory only (not localStorage)
   */
  setAccessToken(token: string): void {
    this.accessToken = token;
    this.hasSession = true;
    this.scheduleProactiveRefresh(token);
  }

  /**
   * Get access token from memory
   */
  getAccessToken(): string | null {
    return this.accessToken;
  }

  /**
   * True when there is an access token and it expires within `withinMs`.
   * Tokens without a readable `exp` are never reported as expiring.
   */
  isAccessTokenExpiring(withinMs: number = EXPIRING_THRESHOLD_MS): boolean {
    if (!this.accessToken) {
      return false;
    }
    const times = readJwtTimes(this.accessToken);
    return times !== null && times.exp - Date.now() < withinMs;
  }

  /**
   * Return an access token that is not about to expire, refreshing first if
   * needed. Use before handing a token to something that cannot retry on 401,
   * such as a WebSocket handshake. If the refresh fails, returns whatever token
   * is held (possibly null) and lets the caller's own error handling take over.
   */
  async ensureFreshAccessToken(): Promise<string | null> {
    if (!this.isAccessTokenExpiring()) {
      return this.accessToken;
    }
    try {
      return await this.refreshAccessToken();
    } catch {
      return this.accessToken;
    }
  }

  /**
   * Clear stored access token
   */
  clearAccessToken(): void {
    this.accessToken = null;
    this.hasSession = false;
    this.cancelProactiveRefresh();
  }

  /**
   * Check authentication status with the server
   * Used on page load to determine if user has a valid session
   */
  async checkAuthStatus(): Promise<AuthStatusResponse> {
    try {
      const response = await fetch('/api/auth/status/', {
        method: 'GET',
        credentials: 'include', // Include cookies
      });

      if (!response.ok) {
        return { has_refresh_token: false, is_authenticated: false };
      }

      const data = await response.json();
      this.hasSession = data.has_refresh_token;
      return data;
    } catch {
      return { has_refresh_token: false, is_authenticated: false };
    }
  }

  /**
   * Login with username/email and password
   * Uses cookie-based endpoint that stores refresh token in httpOnly cookie
   */
  async login(username: string, password: string, replacement?: SessionReplacement): Promise<LoginResponse> {
    const response = await fetch(replacement ? '/api/auth/sessions/replace/' : '/api/auth/login/', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include', // Include cookies for Set-Cookie header
      body: JSON.stringify(replacement ?? { username, password }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw loginError(error);
    }

    const data = await response.json();

    // Store access token in memory (refresh token is in httpOnly cookie)
    this.setAccessToken(data.access);

    return data;
  }

  /**
   * Logout and clear session
   * Calls cookie-based endpoint to blacklist refresh token and clear cookie,
   * and purges all browser storage layers (in-memory, localStorage, sessionStorage,
   * IndexedDB, and Cache Storage) per ASVS 6.6.1.
   */
  async logout(): Promise<void> {
    try {
      await fetch('/api/auth/logout/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include', // Include cookies for deletion
      });
    } catch {
      // Silently handle logout errors - tokens will be cleared regardless
    }

    this.clearAccessToken();
    await this.clearAllBrowserStorage();
  }

  /**
   * Clear all client-side browser storage layers on logout (ASVS 6.6.1):
   * - localStorage: remove legacy/session keys and cached user artifacts
   * - sessionStorage: clear all ephemeral tab state (impersonation, drafts, etc.)
   * - IndexedDB: delete any origin databases if present
   * - Cache Storage: purge any runtime API caches if present
   */
  private async clearAllBrowserStorage(): Promise<void> {
    try {
      if (typeof localStorage !== 'undefined') {
        localStorage.removeItem(LEGACY_TOKEN_STORAGE_KEY);
        localStorage.removeItem('access_token');
        localStorage.removeItem('api_key');
        localStorage.removeItem('offline_signatures');
      }
    } catch {
      // Ignore storage access errors in restricted frames
    }

    try {
      if (typeof sessionStorage !== 'undefined') {
        sessionStorage.clear();
      }
    } catch {
      // Ignore sessionStorage errors
    }

    try {
      if (typeof indexedDB !== 'undefined' && typeof indexedDB.databases === 'function') {
        const dbs = await indexedDB.databases();
        for (const db of dbs) {
          if (db.name) {
            indexedDB.deleteDatabase(db.name);
          }
        }
      }
    } catch {
      // IndexedDB is not used for auth state; ignore if unsupported by environment
    }

    try {
      if (typeof caches !== 'undefined' && typeof caches.keys === 'function') {
        const keys = await caches.keys();
        await Promise.all(keys.map((key) => caches.delete(key)));
      }
    } catch {
      // Ignore Cache Storage API errors
    }
  }

  /**
   * Refresh access token using refresh token from cookie
   * The cookie is automatically sent by the browser
   *
   * Single-flight (same tab): concurrent callers share one in-flight request.
   * Cross-tab: Web Locks API serializes refresh so only one tab rotates the
   * cookie at a time; other tabs wait, then either reuse a BroadcastChannel
   * token or refresh with the already-rotated cookie.
   */
  refreshAccessToken(): Promise<string> {
    if (!this.refreshPromise) {
      this.refreshPromise = this.doRefreshAccessToken().finally(() => {
        this.refreshPromise = null;
      });
    }
    return this.refreshPromise;
  }

  private async doRefreshAccessToken(): Promise<string> {
    const locks = typeof navigator !== 'undefined' ? navigator.locks : undefined;
    if (locks?.request) {
      return locks.request(REFRESH_LOCK_NAME, () => this.refreshWithRetries());
    }
    return this.refreshWithRetries();
  }

  private async refreshWithRetries(): Promise<string> {
    // Another tab may have finished while we waited for the Web Lock.
    const peerToken = this.takeRecentPeerToken();
    if (peerToken) {
      this.setAccessToken(peerToken);
      return peerToken;
    }

    let lastStatus = 0;
    for (let attempt = 0; attempt < REFRESH_ATTEMPTS; attempt++) {
      if (attempt > 0) {
        const delay = REFRESH_RETRY_DELAYS_MS[attempt - 1] ?? 700;
        await new Promise((resolve) => setTimeout(resolve, delay));

        // Peer may have won the race during our backoff.
        const duringBackoff = this.takeRecentPeerToken();
        if (duringBackoff) {
          this.setAccessToken(duringBackoff);
          return duringBackoff;
        }
      }

      const response = await this.postRefresh();
      lastStatus = response.status;

      if (response.ok) {
        const data = (await response.json()) as { access?: string };
        if (!data.access) {
          break;
        }
        this.setAccessToken(data.access);
        this.broadcastRefreshResult(data.access);
        return data.access;
      }

      // Only retry on 401 (typical blacklist race). Other failures are final.
      if (response.status !== 401) {
        break;
      }
    }

    this.clearAccessToken();
    this.broadcastRefreshResult(null);
    throw new Error(
      lastStatus === 401 ? 'Token refresh failed' : `Token refresh failed (${lastStatus})`,
    );
  }

  private postRefresh(): Promise<Response> {
    return fetch('/api/auth/refresh/', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include', // Include refresh token cookie
    });
  }

  /**
   * Check if user is authenticated (has access token in memory)
   */
  isAuthenticated(): boolean {
    return !!this.accessToken;
  }

  /**
   * Check if user has a session (refresh token cookie exists)
   * This is used to determine if we should try to refresh the token
   */
  hasValidSession(): boolean {
    return this.hasSession;
  }

  /**
   * Initialize authentication on page load
   * Checks for existing session and refreshes access token if possible
   */
  async initializeAuth(): Promise<boolean> {
    const status = await this.checkAuthStatus();

    if (status.has_refresh_token) {
      try {
        // Try to get a new access token using the refresh token cookie
        await this.refreshAccessToken();
        return true;
      } catch {
        // Refresh failed - user needs to re-login
        return false;
      }
    }

    return false;
  }

  // ============================================================================
  // Legacy methods for backward compatibility (mobile app support)
  // These use localStorage and the old /api/login/ endpoint
  // ============================================================================

  /**
   * @deprecated Use login() instead - this is for mobile app only
   * SECURITY: This method no longer stores tokens in localStorage to prevent XSS attacks.
   * Tokens should be handled by the mobile app's secure storage (SecureStore).
   */
  async legacyLogin(username: string, password: string): Promise<LoginResponse> {
    const response = await fetch('/api/login/', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ username, password }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw loginError(error);
    }

    const data = await response.json();

    // SECURITY: Do NOT store tokens in localStorage - this is a security risk
    // Mobile apps should use SecureStore (React Native) or Keychain (iOS/Android)
    // Web apps should use cookie-based auth via login() method
    // Tokens are returned in response for mobile app to handle securely

    return data;
  }

  /**
   * @deprecated Use logout() instead - this is for mobile app only
   */
  async legacyLogout(): Promise<void> {
    const stored = localStorage.getItem(LEGACY_TOKEN_STORAGE_KEY);
    if (stored) {
      try {
        const tokens = JSON.parse(stored);
        await fetch('/api/logout/', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ refresh: tokens.refresh }),
        });
      } catch {
        // Silently handle logout errors
      }
    }
    localStorage.removeItem(LEGACY_TOKEN_STORAGE_KEY);
  }

  /**
   * @deprecated Get access token from legacy localStorage storage
   */
  getLegacyAccessToken(): string | null {
    const stored = localStorage.getItem(LEGACY_TOKEN_STORAGE_KEY);
    if (!stored) return null;

    try {
      const tokens = JSON.parse(stored);
      return tokens.access || null;
    } catch {
      return null;
    }
  }
}

// Export singleton instance
export const authService = AuthService.getInstance();
